import {isNil, isNotNil} from 'ramda';

import type {FinancialProductCalculation} from '../schemas/FinancialProductCalculationSchema';
import type {FinancialProductMoney} from '../schemas/FinancialProductMoneySchema';
import {getCurrencyScale} from './getCurrencyScale';
import {isNonNegativeDecimal} from './isNonNegativeDecimal';
import {QuoteDecimal} from './QuoteDecimal';

const PERCENT_BASE = 100;
/** Percent to fraction and per annum to per month in one division, as the backend rounds once. */
const MONTHLY_RATE_BASE = 1200;
/** The backend's `FinancingCalculator::INTERNAL_SCALE`. */
const INTERNAL_SCALE = 24;
/** Digits per factor of `(1 + r)^t` (24 decimals plus the integer digit) plus headroom. */
const POWER_DIGITS_PER_TERM_MONTH = 25;
const POWER_PRECISION_HEADROOM = 32;
/** Discount factors kept at once; a full vehicle-detail matrix needs one per priced cell. */
const DISCOUNT_FACTOR_CACHE_SIZE = 512;

/**
 * `(1 + r)^-t` at 24 decimals by `r.toFixed(24)` and t, the oldest entry evicted first. The exact
 * power runs on up to 2400 digits and a matrix re-prices every cell on each render; the factor
 * depends on r and t alone, so a cached factor is the one the power would give.
 */
const discountFactorCache = new Map<string, QuoteDecimal>();

const toNominalRate = (nominalRate: string | number) => {
  if (typeof nominalRate === 'string') {
    return isNonNegativeDecimal(nominalRate) ? new QuoteDecimal(nominalRate) : null;
  }

  // A number goes through its shortest round-trip string; it is never an intermediate.
  return Number.isFinite(nominalRate) && nominalRate >= 0 ? new QuoteDecimal(nominalRate) : null;
};

/** A fee finer than the currency scale would be truncated by `toFixed`, so it is unusable. */
const isValidFee = (fee: FinancialProductMoney | null, currency: string, scale: number) => {
  if (isNil(fee)) {
    return true;
  }

  const feeScale = getCurrencyScale(fee.amount);

  return fee.currency === currency && isNotNil(feeScale) && feeScale <= scale;
};

const isValidBalloonPercentage = (balloonPaymentPercentage: number | null) =>
  isNil(balloonPaymentPercentage) ||
  (Number.isInteger(balloonPaymentPercentage) && balloonPaymentPercentage >= 0);

const getDiscountFactor = (monthlyRate: QuoteDecimal, termMonths: number) => {
  const key = `${monthlyRate.toFixed(INTERNAL_SCALE)}:${termMonths}`;
  const cachedDiscount = discountFactorCache.get(key);

  if (isNotNil(cachedDiscount)) {
    return cachedDiscount;
  }

  // (1 + r)^t is exact, as the backend's BigDecimal::power: at most 25 digits per factor.
  const PowerDecimal = QuoteDecimal.clone({
    precision: POWER_DIGITS_PER_TERM_MONTH * termMonths + POWER_PRECISION_HEADROOM,
  });
  const power = new PowerDecimal(monthlyRate).plus(1).toPower(termMonths);
  const discount = new QuoteDecimal(1)
    .dividedBy(power)
    .toDecimalPlaces(INTERNAL_SCALE, QuoteDecimal.ROUND_HALF_UP);
  if (discountFactorCache.size >= DISCOUNT_FACTOR_CACHE_SIZE) {
    const oldestKey = discountFactorCache.keys().next().value;

    if (isNotNil(oldestKey)) {
      discountFactorCache.delete(oldestKey);
    }
  }
  discountFactorCache.set(key, discount);

  return discount;
};

/**
 * The monthly annuity before the terminal rounding, mirroring `FinancingCalculator::annuity`.
 *
 * Every division runs in `QuoteDecimal` (160 significant digits, `ROUND_DOWN`) and is then rounded
 * with `toDecimalPlaces(24, ROUND_HALF_UP)`. That equals the backend's exact HALF_UP division:
 * for a positive quotient q, truncation to a grid at least one digit finer than the rounding
 * midpoint m keeps q' ≤ q and q' ≥ m exactly when q ≥ m, because m lies on that grid. Every
 * operand here is positive (rate ≥ 0, balloon < net credit, discount < 1), and 160 digits reach
 * 10^-25 for any quotient below 10^135.
 */
const calculateAnnuity = (
  netCredit: QuoteDecimal,
  balloonPayment: QuoteDecimal,
  nominalRate: QuoteDecimal,
  termMonths: number
) => {
  const monthlyRate = nominalRate
    .dividedBy(MONTHLY_RATE_BASE)
    .toDecimalPlaces(INTERNAL_SCALE, QuoteDecimal.ROUND_HALF_UP);

  if (monthlyRate.isZero()) {
    return netCredit
      .minus(balloonPayment)
      .dividedBy(termMonths)
      .toDecimalPlaces(INTERNAL_SCALE, QuoteDecimal.ROUND_HALF_UP);
  }

  const discount = getDiscountFactor(monthlyRate, termMonths);

  // The numerator is exact; the single division is rounded once.
  return netCredit
    .minus(balloonPayment.times(discount))
    .times(monthlyRate)
    .dividedBy(new QuoteDecimal(1).minus(discount))
    .toDecimalPlaces(INTERNAL_SCALE, QuoteDecimal.ROUND_HALF_UP);
};

/**
 * The backend `FinancingCalculator`, bit for bit, on wire Money strings: the down payment and the
 * balloon payment become money at the currency scale, the annuity runs exactly at 24 decimals,
 * the monthly payment is rounded once to the currency scale, and every total is built from it.
 * The fee joins the gross credit and the total payable, never the interest; the interest may be
 * negative. `nominalRate` and `apr` are carried verbatim.
 *
 * Null when the backend calculator would refuse (the balloon payment does not stay below the net
 * credit) or the input is unusable: a price that is not a positive amount at currency scale, a
 * fee in another currency or with more decimals than the currency scale, a term that is not a positive integer, a down payment outside
 * [0, 100), a negative or non-integer balloon, or a nominal rate that is not a non-negative
 * decimal.
 */
export function calculateFinancingAmounts(input: {
  vehiclePriceWithVat: FinancialProductMoney;
  processingFee: FinancialProductMoney | null;
  termMonths: number;
  downPaymentPercentage: number;
  balloonPaymentPercentage: number | null;
  nominalRate: string | number;
  apr: string | null;
}): FinancialProductCalculation | null {
  const currency = input.vehiclePriceWithVat.currency;
  const scale = getCurrencyScale(input.vehiclePriceWithVat.amount);
  const nominalRate = toNominalRate(input.nominalRate);
  const termMonths = input.termMonths;
  const downPaymentPercentage = input.downPaymentPercentage;
  const balloonPaymentPercentage = input.balloonPaymentPercentage;

  if (
    isNil(scale) ||
    isNil(nominalRate) ||
    !isValidFee(input.processingFee, currency, scale) ||
    !Number.isInteger(termMonths) ||
    termMonths <= 0 ||
    !Number.isInteger(downPaymentPercentage) ||
    downPaymentPercentage < 0 ||
    downPaymentPercentage >= PERCENT_BASE ||
    !isValidBalloonPercentage(balloonPaymentPercentage)
  ) {
    return null;
  }

  const vehiclePrice = new QuoteDecimal(input.vehiclePriceWithVat.amount);

  if (vehiclePrice.isZero()) {
    return null;
  }

  const toCurrencyScale = (value: QuoteDecimal) =>
    value.toDecimalPlaces(scale, QuoteDecimal.ROUND_HALF_UP);
  const toMoney = (value: QuoteDecimal) => ({amount: value.toFixed(scale), currency});

  // P · pct / 100 is a finite decimal well inside the context precision, so the division is exact.
  const downPayment = toCurrencyScale(
    vehiclePrice.times(downPaymentPercentage).dividedBy(PERCENT_BASE)
  );
  const netCredit = vehiclePrice.minus(downPayment);
  const balloonPayment = isNil(balloonPaymentPercentage)
    ? new QuoteDecimal(0)
    : toCurrencyScale(vehiclePrice.times(balloonPaymentPercentage).dividedBy(PERCENT_BASE));

  if (!balloonPayment.lessThan(netCredit)) {
    return null;
  }

  const monthlyPayment = toCurrencyScale(
    calculateAnnuity(netCredit, balloonPayment, nominalRate, termMonths)
  );
  const fee = new QuoteDecimal(input.processingFee?.amount ?? 0);
  const totalInstallments = monthlyPayment.times(termMonths);
  const grossCredit = totalInstallments.plus(balloonPayment).plus(fee);

  return {
    termMonths,
    monthlyPayment: toMoney(monthlyPayment),
    downPaymentPercentage,
    downPaymentAmount: toMoney(downPayment),
    netCredit: toMoney(netCredit),
    grossCredit: toMoney(grossCredit),
    totalInterest: toMoney(totalInstallments.plus(balloonPayment).minus(netCredit)),
    totalPayable: toMoney(downPayment.plus(grossCredit)),
    nominalRate: typeof input.nominalRate === 'string' ? input.nominalRate : nominalRate.toFixed(),
    apr: input.apr,
    balloonPaymentPercentage,
    balloonPaymentAmount: isNotNil(balloonPaymentPercentage) ? toMoney(balloonPayment) : null,
    leasingTargetGroup: null,
    annualMileage: null,
    leasingDownPayment: null,
    monthlyPaymentMode: null,
    leasingTotalMonthlyPayment: null,
    leasingTotalForTerm: null,
  };
}
